import React, { useState } from 'react';
import { Send, ChevronRight, ChevronLeft } from 'lucide-react';
import { emailjsConfig, isEmailjsConfigured, isPreRegisteredEmail } from './config';


export const INPUT_CLASSES =
  'w-full px-4 py-3 bg-brand-warmwhite border border-brand-coffee/20 rounded-md font-body text-brand-charcoal placeholder-brand-stone/60 focus:outline-none focus:border-brand-ember focus:ring-2 focus:ring-brand-ember/30 transition-all';

export function Wordmark({ onDark = false, className = '' }) {
  return (
    <div className={className}>
      <div className='font-display font-bold text-xl leading-none tracking-wide'>
        <span className={onDark ? 'text-brand-cream' : 'text-brand-coffee'}>
          HORIZON{' '}
        </span>
        <span className='text-brand-ember'>RCM</span>
      </div>
      <div
        className={`font-body text-[8px] font-semibold uppercase mt-1 ${
          onDark ? 'text-brand-cream/60' : 'text-brand-stone'
        }`}
        style={{ letterSpacing: '0.28em' }}
      >
        Pathology Revenue Cycle Management
      </div>
    </div>
  );
}

export function Eyebrow({ children, className = '' }) {
  return (
    <p
      className={`font-body text-xs font-semibold uppercase ${className}`}
      style={{ letterSpacing: '0.3em' }}
    >
      {children}
    </p>
  );
}

export function CtaChevrons({ side }) {
  const opacities = side === 'left' ? [0.2, 0.5, 0.9] : [0.9, 0.5, 0.2];
  const Icon = side === 'left' ? ChevronRight : ChevronLeft;

  return (
    <span
      className={`inline-flex items-center -space-x-2 ${side === 'left' ? 'mr-3' : 'ml-3'}`}
      aria-hidden='true'
    >
      {opacities.map((opacity, i) => (
        <Icon
          key={i}
          size={18}
          className='text-brand-ember'
          style={{ opacity }}
          strokeWidth={2.5}
        />
      ))}
    </span>
  );
}

// ─── Shared email primitives ────────────────────────────────────────────────

const EMAIL_HEADER = `
  <table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FAF7F2;border-top:4px solid #D2691E;">
    <tr>
      <td style="padding:28px 40px 20px;">
        <span style="font-family:'Georgia',serif;font-weight:bold;font-size:20px;color:#3D2817;letter-spacing:0.05em;">HORIZON </span>
        <span style="font-family:'Georgia',serif;font-weight:bold;font-size:20px;color:#D2691E;letter-spacing:0.05em;">RCM</span>
        <div style="font-family:'Arial',sans-serif;font-size:9px;font-weight:600;letter-spacing:0.28em;text-transform:uppercase;color:#6B6259;margin-top:4px;">Pathology Revenue Cycle Management</div>
      </td>
    </tr>
    <tr><td style="padding:0 40px;"><div style="height:1px;background-color:#D2691E22;"></div></td></tr>`;

const EMAIL_FOOTER = `
    <tr>
      <td style="background-color:#1B2A4E;padding:16px 40px;text-align:center;">
        <p style="margin:0;font-family:'Arial',sans-serif;font-size:10px;font-weight:600;letter-spacing:0.2em;text-transform:uppercase;color:#F5EFE699;">Exclusive Pathology Billing &nbsp;·&nbsp; 100% US-Based &nbsp;·&nbsp; horizon-rcm.com</p>
      </td>
    </tr>
  </table>`;

const EMAIL_SIGNATURE = `
  <div style="border-top:1px solid #D2691E33;padding-top:20px;margin-top:8px;">
    <p style="margin:0 0 2px;font-weight:bold;font-size:15px;">Best Regards,</p>
    <p style="margin:0 0 2px;font-size:15px;">Jack Conley</p>
    <p style="margin:0 0 8px;font-size:13px;color:#6B6259;">Founder and CEO, Horizon RCM</p>
    <p style="margin:0;font-size:13px;">
      <a href="mailto:main@horizon-rcm.com" style="color:#D2691E;text-decoration:none;">main@horizon-rcm.com</a>
      <span style="color:#6B6259;"> &nbsp;·&nbsp; </span>
      <a href="tel:+18882576123" style="color:#6B6259;text-decoration:none;">(888) 257-6123</a>
    </p>
  </div>`;

const CASE_STUDY_SECTION = `
    <tr><td style="padding:24px 40px 0;"><div style="height:1px;background-color:#D2691E22;"></div></td></tr>
    <tr>
      <td style="padding:24px 40px 32px;">
        <p style="margin:0 0 8px;font-family:'Arial',sans-serif;font-size:9px;font-weight:700;letter-spacing:0.3em;text-transform:uppercase;color:#D2691E;">Client Success Story</p>
        <p style="margin:0 0 16px;font-family:'Georgia',serif;font-weight:bold;font-size:17px;color:#3D2817;line-height:1.4;">How We Helped an Independent Pathology Lab Reclaim Its Revenue</p>
        <table width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 20px;background-color:#1B2A4E;">
          <tr>
            <td style="width:33%;text-align:center;padding:14px 8px;">
              <div style="font-family:'Georgia',serif;font-weight:bold;font-size:22px;color:#D2691E;">&gt;92%</div>
              <div style="font-family:'Arial',sans-serif;font-size:9px;font-weight:600;letter-spacing:0.15em;text-transform:uppercase;color:#F5EFE699;margin-top:4px;">Net Collection Rate</div>
            </td>
            <td style="width:33%;text-align:center;padding:14px 8px;border-left:1px solid #F5EFE620;border-right:1px solid #F5EFE620;">
              <div style="font-family:'Georgia',serif;font-weight:bold;font-size:22px;color:#D2691E;">100%</div>
              <div style="font-family:'Arial',sans-serif;font-size:9px;font-weight:600;letter-spacing:0.15em;text-transform:uppercase;color:#F5EFE699;margin-top:4px;">Modifier Accuracy</div>
            </td>
            <td style="width:33%;text-align:center;padding:14px 8px;">
              <div style="font-family:'Georgia',serif;font-weight:bold;font-size:22px;color:#D2691E;">Day 1</div>
              <div style="font-family:'Arial',sans-serif;font-size:9px;font-weight:600;letter-spacing:0.15em;text-transform:uppercase;color:#F5EFE699;margin-top:4px;">Compliance Fix</div>
            </td>
          </tr>
        </table>
        <p style="margin:0 0 14px;font-family:'Georgia',serif;font-size:14px;line-height:1.7;color:#3D2817;">An independent Texas pathology laboratory had cycled through multiple billing vendors, each leaving behind compounding errors. The most damaging: every claim was submitted using global billing codes instead of Modifier 26 — meaning the lab was consistently reimbursed below what it was owed, with growing payer overpayment exposure.</p>
        <p style="margin:0 0 14px;font-family:'Georgia',serif;font-size:14px;line-height:1.7;color:#3D2817;">Horizon RCM corrected the modifier issue on day one and rebuilt the workflow around structured denial management, on-demand claim-level transparency, and proactive credentialing support. Within the first full quarter, the net collection rate exceeded 92% — and has held there consistently since.</p>
        <p style="margin:0;font-family:'Georgia',serif;font-size:13px;line-height:1.6;color:#6B6259;font-style:italic;">"Spot checks that once produced anxiety now produce confirmation." — Independent Pathology Lab, Texas</p>
      </td>
    </tr>`;

const CALENDLY_BUTTON = `
    <table cellpadding="0" cellspacing="0" style="margin:0 0 24px;">
      <tr>
        <td style="background-color:#D2691E;padding:14px 32px;">
          <a href="https://calendly.com/horizon-rcm" target="_blank" style="font-family:'Arial',sans-serif;font-size:13px;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;color:#FAF7F2;text-decoration:none;">Schedule Your Discovery Call &rarr;</a>
        </td>
      </tr>
    </table>`;

// ─── Email builders ─────────────────────────────────────────────────────────

export function buildHomepageConfirmationEmail() {
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background-color:#F5EFE6;font-family:'Georgia',serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#F5EFE6;padding:32px 16px;">
    <tr><td align="center">
      ${EMAIL_HEADER}
      <tr>
        <td style="padding:32px 40px 0;color:#3D2817;font-family:'Georgia',serif;font-size:15px;line-height:1.7;">
          <p style="margin:0 0 18px;">Thank you for reaching out — we're glad you found us!</p>
          <p style="margin:0 0 18px;">We received your inquiry about pathology revenue cycle management, and we'd love to learn more about your practice and how we can help.</p>
          <p style="margin:0 0 12px;">I'd like to invite you to a brief discovery meeting where we can:</p>
          <table cellpadding="0" cellspacing="0" style="margin:0 0 18px;">
            <tr>
              <td style="width:18px;vertical-align:top;padding-top:2px;color:#D2691E;font-size:18px;line-height:1;">&#8226;</td>
              <td style="font-family:'Georgia',serif;font-size:15px;line-height:1.7;color:#3D2817;padding-left:8px;">Learn about your current billing workflows and pain points</td>
            </tr>
            <tr>
              <td style="width:18px;vertical-align:top;padding-top:2px;color:#D2691E;font-size:18px;line-height:1;">&#8226;</td>
              <td style="font-family:'Georgia',serif;font-size:15px;line-height:1.7;color:#3D2817;padding-left:8px;">Share how we've helped similar groups improve collections</td>
            </tr>
            <tr>
              <td style="width:18px;vertical-align:top;padding-top:2px;color:#D2691E;font-size:18px;line-height:1;">&#8226;</td>
              <td style="font-family:'Georgia',serif;font-size:15px;line-height:1.7;color:#3D2817;padding-left:8px;">Answer any questions you have about our services</td>
            </tr>
          </table>
          <p style="margin:0 0 18px;">No follow-up needed — just click below and pick a time that works for you:</p>
          ${CALENDLY_BUTTON}
          <p style="margin:0 0 32px;font-size:13px;color:#6B6259;">Or simply reply to this email and we can coordinate directly.</p>
          <p style="margin:0 0 24px;">We look forward to connecting!</p>
          ${EMAIL_SIGNATURE}
        </td>
      </tr>
      ${CASE_STUDY_SECTION}
      ${EMAIL_FOOTER}
    </td></tr>
  </table>
</body>
</html>`.trim();
}

export function buildLeadConfirmationEmail() {
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background-color:#F5EFE6;font-family:'Georgia',serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#F5EFE6;padding:32px 16px;">
    <tr><td align="center">
      ${EMAIL_HEADER}
      <tr>
        <td style="padding:32px 40px 0;color:#3D2817;font-family:'Georgia',serif;font-size:15px;line-height:1.7;">
          <p style="margin:0 0 18px;">We received your consultation request and we're glad you took this step.</p>
          <p style="margin:0 0 18px;">Pathology billing done right makes a measurable difference in revenue, compliance, and peace of mind. We'd love to learn about your practice and show you exactly what that looks like in practice.</p>
          <p style="margin:0 0 12px;">In our discovery meeting, we'll:</p>
          <table cellpadding="0" cellspacing="0" style="margin:0 0 18px;">
            <tr>
              <td style="width:18px;vertical-align:top;padding-top:2px;color:#D2691E;font-size:18px;line-height:1;">&#8226;</td>
              <td style="font-family:'Georgia',serif;font-size:15px;line-height:1.7;color:#3D2817;padding-left:8px;">Review your current billing setup and identify gaps</td>
            </tr>
            <tr>
              <td style="width:18px;vertical-align:top;padding-top:2px;color:#D2691E;font-size:18px;line-height:1;">&#8226;</td>
              <td style="font-family:'Georgia',serif;font-size:15px;line-height:1.7;color:#3D2817;padding-left:8px;">Walk through how Horizon RCM's model fits your lab's needs</td>
            </tr>
            <tr>
              <td style="width:18px;vertical-align:top;padding-top:2px;color:#D2691E;font-size:18px;line-height:1;">&#8226;</td>
              <td style="font-family:'Georgia',serif;font-size:15px;line-height:1.7;color:#3D2817;padding-left:8px;">Answer every question — no pressure, no obligation</td>
            </tr>
          </table>
          <p style="margin:0 0 18px;">Book your time directly below — it takes less than a minute:</p>
          ${CALENDLY_BUTTON}
          <p style="margin:0 0 32px;font-size:13px;color:#6B6259;">Prefer email? Just reply here and we'll coordinate directly.</p>
          <p style="margin:0 0 24px;">Looking forward to it,</p>
          ${EMAIL_SIGNATURE}
        </td>
      </tr>
      ${CASE_STUDY_SECTION}
      ${EMAIL_FOOTER}
    </td></tr>
  </table>
</body>
</html>`.trim();
}

const CASE_STUDY_URL = 'https://docs.google.com/document/d/1U1aRVuOxF7C4T-XCpKFM9C4z8El7qdJw/edit?usp=sharing';

export function buildCalculatorConfirmationEmail() {
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background-color:#F5EFE6;font-family:'Georgia',serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#F5EFE6;padding:32px 16px;">
    <tr><td align="center">
      ${EMAIL_HEADER}
      <tr>
        <td style="padding:32px 40px 0;color:#3D2817;font-family:'Georgia',serif;font-size:15px;line-height:1.7;">
          <p style="margin:0 0 18px;">Thanks for using our revenue calculator — we're preparing your personalized breakdown.</p>
          <p style="margin:0 0 18px;">The estimate you saw is a starting point based on industry benchmarks. In your free revenue analysis, we'll use your actual payer mix, denial history, and billing data to give you a precise picture of where revenue is going and what recovery looks like.</p>
          <p style="margin:0 0 12px;">On our discovery call, we'll:</p>
          <table cellpadding="0" cellspacing="0" style="margin:0 0 18px;">
            <tr>
              <td style="width:18px;vertical-align:top;padding-top:2px;color:#D2691E;font-size:18px;line-height:1;">&#8226;</td>
              <td style="font-family:'Georgia',serif;font-size:15px;line-height:1.7;color:#3D2817;padding-left:8px;">Walk through your calculator results with your real numbers</td>
            </tr>
            <tr>
              <td style="width:18px;vertical-align:top;padding-top:2px;color:#D2691E;font-size:18px;line-height:1;">&#8226;</td>
              <td style="font-family:'Georgia',serif;font-size:15px;line-height:1.7;color:#3D2817;padding-left:8px;">Identify the highest-impact denial and follow-up gaps</td>
            </tr>
            <tr>
              <td style="width:18px;vertical-align:top;padding-top:2px;color:#D2691E;font-size:18px;line-height:1;">&#8226;</td>
              <td style="font-family:'Georgia',serif;font-size:15px;line-height:1.7;color:#3D2817;padding-left:8px;">Outline a practical plan to recover revenue — no pressure, no obligation</td>
            </tr>
          </table>
          <p style="margin:0 0 18px;">Pick a time that works for you:</p>
          ${CALENDLY_BUTTON}
          <p style="margin:0 0 32px;font-size:13px;color:#6B6259;">Prefer email? Just reply here and we'll coordinate directly.</p>
          <p style="margin:0 0 24px;">Looking forward to connecting,</p>
          ${EMAIL_SIGNATURE}
        </td>
      </tr>
      ${CASE_STUDY_SECTION}
      ${EMAIL_FOOTER}
    </td></tr>
  </table>
</body>
</html>`.trim();
}

export function buildCaseStudyEmail() {
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background-color:#F5EFE6;font-family:'Georgia',serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#F5EFE6;padding:32px 16px;">
    <tr><td align="center">
      ${EMAIL_HEADER}
      <tr>
        <td style="padding:32px 40px;color:#3D2817;font-family:'Georgia',serif;font-size:15px;line-height:1.7;">
          <p style="margin:0 0 18px;">Here's the case study you requested — it walks through exactly how we work and what we deliver.</p>
          <p style="margin:0 0 24px;">Click below to open it — no sign-in required:</p>
          <!-- Case Study CTA -->
          <table cellpadding="0" cellspacing="0" style="margin:0 0 28px;">
            <tr>
              <td style="background-color:#3D2817;padding:14px 32px;">
                <a href="${CASE_STUDY_URL}" target="_blank" style="font-family:'Arial',sans-serif;font-size:13px;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;color:#FAF7F2;text-decoration:none;">Read the Case Study &rarr;</a>
              </td>
            </tr>
          </table>
          <!-- Stats teaser -->
          <table width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 28px;background-color:#1B2A4E;">
            <tr>
              <td style="width:33%;text-align:center;padding:14px 8px;">
                <div style="font-family:'Georgia',serif;font-weight:bold;font-size:22px;color:#D2691E;">&gt;92%</div>
                <div style="font-family:'Arial',sans-serif;font-size:9px;font-weight:600;letter-spacing:0.15em;text-transform:uppercase;color:#F5EFE699;margin-top:4px;">Net Collection Rate</div>
              </td>
              <td style="width:33%;text-align:center;padding:14px 8px;border-left:1px solid #F5EFE620;border-right:1px solid #F5EFE620;">
                <div style="font-family:'Georgia',serif;font-weight:bold;font-size:22px;color:#D2691E;">100%</div>
                <div style="font-family:'Arial',sans-serif;font-size:9px;font-weight:600;letter-spacing:0.15em;text-transform:uppercase;color:#F5EFE699;margin-top:4px;">Modifier Accuracy</div>
              </td>
              <td style="width:33%;text-align:center;padding:14px 8px;">
                <div style="font-family:'Georgia',serif;font-weight:bold;font-size:22px;color:#D2691E;">Day 1</div>
                <div style="font-family:'Arial',sans-serif;font-size:9px;font-weight:600;letter-spacing:0.15em;text-transform:uppercase;color:#F5EFE699;margin-top:4px;">Compliance Fix</div>
              </td>
            </tr>
          </table>
          <p style="margin:0 0 18px;">If anything in there resonates with your situation, I'd love to connect for a quick discovery call.</p>
          ${CALENDLY_BUTTON}
          <p style="margin:0 0 32px;font-size:13px;color:#6B6259;">Or reply to this email and we'll find a time that works.</p>
          ${EMAIL_SIGNATURE}
        </td>
      </tr>
      ${EMAIL_FOOTER}
    </td></tr>
  </table>
</body>
</html>`.trim();
}

// ─── Internal notifications ──────────────────────────────────────────────────

function buildPlainNotificationMessage({ fromEmail, fromName, source, submittedAt }) {
  return [
    source,
    fromName && `Name: ${fromName}`,
    `From: ${fromEmail}`,
    `Submitted: ${submittedAt}`,
  ]
    .filter(Boolean)
    .join('\n');
}

export async function sendInternalNotification({
  fromEmail,
  fromName = '',
  source,
  replyTo,
}) {
  if (!isEmailjsConfigured()) return;

  const emailjs = (await import('@emailjs/browser')).default;
  const submittedAt = new Date().toLocaleString('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  });

  const message = buildPlainNotificationMessage({
    fromEmail,
    fromName,
    source,
    submittedAt,
  });

  const response = await emailjs.send(
    emailjsConfig.serviceId,
    emailjsConfig.notificationTemplateId,
    {
      from_email: fromEmail,
      from_name: fromName,
      reply_to: replyTo || fromEmail,
      source,
      submitted_at: submittedAt,
      message,
    },
    emailjsConfig.publicKey
  );

  if (response.status !== 200) {
    throw new Error(`Notification email failed (status ${response.status})`);
  }
}

// ─── ConsultationForm ────────────────────────────────────────────────────────

export function ConsultationForm({
  source = 'New consultation request from website contact form.',
  title = 'Request a Free Consultation',
  description = "Enter your email below and we'll get back to you as soon as possible",
  idPrefix = '',
  emailBuilder = buildHomepageConfirmationEmail,
  emailSubject = 'Thank You for Reaching Out – Horizon RCM',
  successMessage = "Thank you! We've sent a confirmation to your inbox with next steps.",
  initialEmail = '',
  initialName = '',
  submitLabel = 'Send Message',
  skipConfirmation = false,
  showNameField = false,
}) {
  const [formData, setFormData] = useState({ name: initialName, email: initialEmail });

  React.useEffect(() => {
    if (initialEmail || initialName) {
      setFormData(prev => ({
        ...prev,
        ...(initialName ? { name: initialName } : {}),
        ...(initialEmail ? { email: initialEmail } : {}),
      }));
    }
  }, [initialEmail, initialName]);
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: null,
  });

  const isValidEmail = value =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'name' && nameError) setNameError('');
    if (name === 'email' && emailError) setEmailError('');
    if (formStatus.error) setFormStatus(prev => ({ ...prev, error: null }));
  };

  const handleFormSubmit = async e => {
    e.preventDefault();

    if (showNameField && !formData.name.trim()) {
      setNameError('Name is required.');
      return;
    }
    if (!formData.email.trim()) {
      setEmailError('Email address is required.');
      return;
    }
    if (!isValidEmail(formData.email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setNameError('');
    setEmailError('');

    setFormStatus({ submitting: true, submitted: false, error: null });

    const submittedAt = new Date().toLocaleString('en-US', {
      dateStyle: 'long',
      timeStyle: 'short',
    });

    const notificationSource = [
      source,
      formData.name && `Name: ${formData.name}`,
      `Email: ${formData.email}`,
    ]
      .filter(Boolean)
      .join('\n');

    const shouldSkipConfirmation =
      skipConfirmation || isPreRegisteredEmail(formData.email);

    try {
      if (isEmailjsConfigured()) {
        await sendInternalNotification({
          fromEmail: formData.email,
          fromName: formData.name,
          source: notificationSource,
        });

        if (!shouldSkipConfirmation && emailjsConfig.confirmationTemplateId) {
          const emailjs = (await import('@emailjs/browser')).default;
          const confirmResponse = await emailjs.send(
            emailjsConfig.serviceId,
            emailjsConfig.confirmationTemplateId,
            {
              to_email: formData.email,
              subject: emailSubject,
              html_message: emailBuilder(),
            },
            emailjsConfig.publicKey
          );
          if (confirmResponse.status !== 200)
            throw new Error(`Confirmation email failed (status ${confirmResponse.status})`);
        }
      } else {
        // Fallback: open the user's mail client pre-filled for main@horizon-rcm.com
        const emailBody =
          `New Consultation Request – Horizon RCM Website\n\n` +
          `Name: ${formData.name}\n` +
          `From: ${formData.email}\n` +
          `Source: ${notificationSource}\n` +
          `Submitted: ${submittedAt}`;
        const mailtoUrl =
          `mailto:main@horizon-rcm.com` +
          `?subject=${encodeURIComponent('New Consultation Request – Horizon RCM')}` +
          `&body=${encodeURIComponent(emailBody)}`;
        window.open(mailtoUrl, '_blank');
      }

      setFormStatus({ submitting: false, submitted: true, error: null });
      setFormData({ name: '', email: '' });
      setTimeout(() => setFormStatus(prev => ({ ...prev, submitted: false })), 5000);
    } catch (error) {
      setFormStatus({
        submitting: false,
        submitted: false,
        error:
          error.message ||
          'Failed to send message. Please try again or email us directly at main@horizon-rcm.com',
      });
    }
  };

  const nameId = idPrefix ? `${idPrefix}-name` : 'name';
  const emailId = idPrefix ? `${idPrefix}-email` : 'email';

  return (
    <>
      <h3 className='font-display font-bold text-brand-coffee text-2xl mb-3'>{title}</h3>
      <p className='font-body text-brand-stone mb-8'>{description}</p>
      {formStatus.submitted && !formStatus.error && (
        <div className='mb-6 p-4 bg-brand-navy/5 border border-brand-navy/30 text-center'>
          <p className='font-body text-brand-navy font-semibold'>
            {successMessage}
          </p>
        </div>
      )}
      {formStatus.error && (
        <div className='mb-6 p-4 bg-brand-ember/10 border border-brand-ember/40 text-center'>
          <p className='font-body text-brand-ember font-semibold'>{formStatus.error}</p>
        </div>
      )}
      <form onSubmit={handleFormSubmit} className='space-y-6' noValidate>
        {showNameField && (
          <div>
            <label
              htmlFor={nameId}
              className='block font-body text-sm font-medium text-brand-coffee mb-2'
            >
              Name <span className='text-brand-ember'>*</span>
            </label>
            <input
              type='text'
              id={nameId}
              name='name'
              value={formData.name}
              onChange={handleInputChange}
              required
              aria-describedby={nameError ? `${nameId}-error` : undefined}
              className={`${INPUT_CLASSES}${nameError ? ' border-brand-ember ring-2 ring-brand-ember/30' : ''}`}
              placeholder='Your name'
            />
            {nameError && (
              <p id={`${nameId}-error`} className='mt-2 font-body text-sm text-brand-ember'>
                {nameError}
              </p>
            )}
          </div>
        )}
        <div>
          <label
            htmlFor={emailId}
            className='block font-body text-sm font-medium text-brand-coffee mb-2'
          >
            Email <span className='text-brand-ember'>*</span>
          </label>
          <input
            type='email'
            id={emailId}
            name='email'
            value={formData.email}
            onChange={handleInputChange}
            required
            aria-describedby={emailError ? `${emailId}-error` : undefined}
            className={`${INPUT_CLASSES}${emailError ? ' border-brand-ember ring-2 ring-brand-ember/30' : ''}`}
            placeholder='your.email@example.com'
          />
          {emailError && (
            <p id={`${emailId}-error`} className='mt-2 font-body text-sm text-brand-ember'>
              {emailError}
            </p>
          )}
        </div>
        <div>
          <button
            type='submit'
            disabled={formStatus.submitting}
            className='inline-flex items-center justify-center bg-brand-navy hover:bg-brand-coffee disabled:bg-brand-navy/50 disabled:cursor-not-allowed text-brand-warmwhite font-body font-semibold text-sm uppercase py-4 px-10 transition-colors duration-300'
            style={{ letterSpacing: '0.18em' }}
          >
            {formStatus.submitting ? (
              <>
                <span className='mr-2'>Sending...</span>
                <div className='w-4 h-4 border-2 border-brand-warmwhite border-t-transparent rounded-full animate-spin'></div>
              </>
            ) : (
              <>
                <Send className='mr-2' size={18} />
                {submitLabel}
              </>
            )}
          </button>
        </div>
      </form>
    </>
  );
}
